import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, Megaphone, X } from 'lucide-react';
import useLiveNotifications from '../hooks/useLiveNotifications';

// In-page banner for the live admin notifications, rendered at the top of the
// Dashboard. Complements the login overlay and the bell: those are easy to
// close once and forget, this one sits in the middle of the page the user
// looks at most.
//
// Closing it only hides it for the current visit to the page — it's back
// every time the Dashboard mounts, for as long as a notification is live.
const NotificationBanner = () => {
  const { eligible, notifications } = useLiveNotifications();
  const [index, setIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (index >= notifications.length) setIndex(0);
  }, [notifications, index]);

  if (!eligible || dismissed || notifications.length === 0) return null;

  const current = notifications[Math.min(index, notifications.length - 1)];
  const total = notifications.length;
  const go = (step) => setIndex((i) => (i + step + total) % total);

  return (
    <div
      role="status"
      data-testid="notification-banner"
      className="relative mb-8 overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 p-5 text-white shadow-lg shadow-blue-500/20 sm:p-6"
    >
      <button
        type="button"
        onClick={() => setDismissed(true)}
        data-testid="notification-banner-close"
        aria-label="Cerrar"
        className="absolute top-3 right-3 rounded-full p-1.5 text-white/80 transition-colors hover:bg-white/15 hover:text-white"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex flex-col gap-4 pr-6 sm:flex-row sm:items-center">
        <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/15">
          <Megaphone className="h-5 w-5 origin-center animate-bell-ring" />
          <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-300 opacity-75" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-amber-400" />
          </span>
        </div>

        <p className="flex-1 whitespace-pre-line text-sm font-medium leading-relaxed sm:text-base">
          {current.message}
        </p>

        {current.link_url && (
          <a
            href={current.link_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center justify-center rounded-lg bg-white px-4 py-2 text-sm font-semibold text-blue-700 shadow transition-colors hover:bg-blue-50"
          >
            {current.link_label || 'Ingresa aquí'}
            <ExternalLink className="ml-2 h-4 w-4" />
          </a>
        )}
      </div>

      {total > 1 && (
        <div className="mt-4 flex items-center justify-end gap-2 text-xs text-white/80">
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="Anterior"
            className="rounded-full p-1 transition-colors hover:bg-white/15 hover:text-white"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span>{index + 1} / {total}</span>
          <button
            type="button"
            onClick={() => go(1)}
            aria-label="Siguiente"
            className="rounded-full p-1 transition-colors hover:bg-white/15 hover:text-white"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationBanner;
